'use client'

import Link from 'next/link'
import { Download, Apple, Smartphone } from 'lucide-react'

interface AppLink {
  label: string
  platform: 'ios' | 'android'
  type: 'user' | 'business'
  href: string | null
  available: boolean
}

const appLinks: AppLink[] = [
  {
    label: 'iPhone',
    platform: 'ios',
    type: 'user',
    href: 'https://apps.apple.com/mx/app/rombex-crossborder-payments/id6747320009?l=en-GB',
    available: true,
  },
  {
    label: 'Android',
    platform: 'android',
    type: 'user',
    href: 'https://play.google.com/store/apps/details?id=com.exchange.global_exchange&pcampaignid=web_share',
    available: true,
  },
  {
    label: 'iPhone',
    platform: 'ios',
    type: 'business',
    href: null,
    available: false,
  },
  {
    label: 'Android',
    platform: 'android',
    type: 'business',
    href: null,
    available: false,
  },
]

export function AppDownload() {
  const userApps = appLinks.filter((app) => app.type === 'user')
  const businessApps = appLinks.filter((app) => app.type === 'business')

  return (
    <section className="relative border-b border-border bg-gradient-to-b from-background via-brand/2 to-background py-20">
      <div className="mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand/10 px-4 py-1.5">
            <Download className="size-4 text-brand" />
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">Download Apps</span>
          </div>
          <h2 className="mb-3 text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl">
            Move Money Anywhere, Anytime
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Access Rombex on your mobile device. Send money, check balances, and manage payments on the go.
          </p>
        </div>

        {/* Download Cards */}
        <div className="grid gap-8 lg:grid-cols-2">
          {/* User App */}
          <div className="group rounded-2xl border border-border bg-gradient-to-br from-background to-background/50 p-8 transition-all hover:border-brand/50 hover:shadow-lg">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex size-12 items-center justify-center rounded-full bg-brand/10">
                <Smartphone className="size-6 text-brand" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">User App</h3>
                <p className="text-sm text-muted-foreground">For individuals</p>
              </div>
            </div>

            <p className="mb-6 leading-relaxed text-muted-foreground">
              Send and receive money globally. Track transactions, manage wallets, and stay connected to your finances.
            </p>

            <div className="flex gap-3">
              {userApps.map((app) => (
                <a
                  key={`${app.platform}-${app.type}`}
                  href={app.href || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 font-medium transition-all ${
                    app.available
                      ? 'bg-brand text-brand-foreground hover:bg-brand/90 hover:shadow-md'
                      : 'cursor-not-allowed bg-muted text-muted-foreground opacity-50'
                  }`}
                  onClick={(e) => {
                    if (!app.available) {
                      e.preventDefault()
                    }
                  }}
                >
                  {app.platform === 'ios' ? (
                    <Apple className="size-5" />
                  ) : (
                    <svg className="size-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 6c0-1.1.9-2 2-2h2l2-2h4l2 2h2c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V6zm2 0v12h14V6H5z"/>
                    </svg>
                  )}
                  {app.platform === 'ios' ? 'App Store' : 'Play Store'}
                </a>
              ))}
            </div>

            <p className="mt-4 text-xs text-muted-foreground">Available for iOS and Android</p>
          </div>

          {/* Business App */}
          <div className="group rounded-2xl border border-border/50 bg-gradient-to-br from-background/50 to-background/30 p-8 opacity-60 transition-all">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                <svg className="size-6 text-muted-foreground" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">Business App</h3>
                <p className="text-sm text-muted-foreground">For merchants</p>
              </div>
            </div>

            <p className="mb-6 leading-relaxed text-muted-foreground">
              Manage your business payments, track settlements, and oversee transactions across multiple accounts.
            </p>

            <div className="flex gap-3">
              {businessApps.map((app) => (
                <button
                  key={`${app.platform}-${app.type}`}
                  disabled
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-muted px-4 py-3 font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                >
                  {app.platform === 'ios' ? (
                    <Apple className="size-5" />
                  ) : (
                    <svg className="size-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 6c0-1.1.9-2 2-2h2l2-2h4l2 2h2c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V6zm2 0v12h14V6H5z"/>
                    </svg>
                  )}
                  Coming Soon
                </button>
              ))}
            </div>

            <p className="mt-4 text-xs text-muted-foreground">Launching soon on iOS and Android</p>
          </div>
        </div>

        {/* Info Text */}
        <div className="mt-12 rounded-xl border border-brand/10 bg-brand/5 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Have questions? <Link href="/contact" className="font-semibold text-brand transition-colors hover:text-brand/90">
              Contact our team
            </Link>{' '}
            for business app access or other inquiries.
          </p>
        </div>
      </div>
    </section>
  )
}
